import React, { useRef } from 'react';
import { FireOutlined } from '@ant-design/icons';
import { KdsOrderWithTime, TIME_THRESHOLDS, formatElapsedTime } from './kdsHelpers';
import {
  KdsOrderAction,
  KdsOrderHandlers,
  KdsOrderItems,
  getOrderFlags,
  passSecondsOf,
  resolveOrderAction,
} from './kdsOrderParts';

export interface KdsLane {
  key: string;
  className: string;
  label: string;
  orders: KdsOrderWithTime[];
}

interface KitchenRailsViewProps extends KdsOrderHandlers {
  lanes: KdsLane[];
  acceptedOrders: Set<string>;
}

// Los tres rieles de trabajo se ven siempre (vacío también dice algo: "no hay nuevas");
// el de despachadas sólo cuando trae algo, igual que en la vista de carriles.
const ALWAYS_VISIBLE = new Set(['new', 'prep', 'ready']);

// Una comanda recién llegada a su riel (o recién tocada) no acepta otro toque en este lapso:
// un doble toque en una pantalla con grasa no debe aceptar Y marcar lista de un jalón.
const TAP_GUARD_MS = 900;

const STATE_LABEL: Record<string, string> = {
  new: 'Nueva',
  prep: 'Preparando',
  ready: 'Lista',
  done: 'Despachada',
};

interface TicketProps {
  order: KdsOrderWithTime;
  laneKey: string;
  ringing: boolean;
  handlers: KdsOrderHandlers;
}

const TicketTimer: React.FC<{ order: KdsOrderWithTime }> = ({ order }) => {
  const { isReady, isLate } = getOrderFlags(order);
  if (isReady) {
    const passSeconds = passSecondsOf(order);
    return (
      <span className="kds-ticket__time kds-ticket__time--ready">
        {passSeconds !== null ? formatElapsedTime(passSeconds) : '✓'}
        {passSeconds !== null && <small>en el pase</small>}
      </span>
    );
  }
  if (isLate) {
    return (
      <span className="kds-ticket__time kds-ticket__time--bad">
        {formatElapsedTime(order.elapsedSeconds)}
        <small>¿olvidada?</small>
      </span>
    );
  }
  const tone = order.elapsedSeconds >= TIME_THRESHOLDS.YELLOW
    ? ' kds-ticket__time--bad'
    : order.elapsedSeconds >= TIME_THRESHOLDS.GREEN
      ? ' kds-ticket__time--warn'
      : '';
  return <span className={`kds-ticket__time${tone}`}>{formatElapsedTime(order.elapsedSeconds)}</span>;
};

/**
 * Una comanda de papel colgada del riel. Tocarla hace lo mismo que el botón de la tarjeta
 * (aceptar → listo → despachar). Las salidas de emergencia ("Forzar despacho") no se
 * disparan con un toque en cualquier parte: llevan su propio botón al pie.
 */
const HangingTicket: React.FC<TicketProps> = ({ order, laneKey, ringing, handlers }) => {
  const { isLate } = getOrderFlags(order);
  const action: KdsOrderAction = resolveOrderAction(order, handlers);
  const tapAction = action?.kind === 'button' && action.tone !== 'danger' ? action : null;
  const busy = !!tapAction?.loading;
  // Al cambiar de estado la comanda se monta en otro riel, así que esto también cubre el
  // toque que llega justo después de que se movió.
  const guardUntil = useRef(Date.now() + TAP_GUARD_MS);

  const run = () => {
    if (!tapAction || busy || Date.now() < guardUntil.current) return;
    guardUntil.current = Date.now() + TAP_GUARD_MS;
    tapAction.run();
  };

  const stateClass = isLate ? 'kds-ticket--late' : `kds-ticket--${laneKey}`;

  const renderFooterAction = () => {
    if (!action) return null;
    if (action.kind === 'button' && action.tone === 'danger') {
      return (
        <button
          type="button"
          className="kds-ticket__danger"
          onClick={(e) => {
            e.stopPropagation();
            if (!action.loading) action.run();
          }}
        >
          {action.loading ? '…' : action.label}
        </button>
      );
    }
    if (action.kind === 'button') {
      return <span className="kds-ticket__hint">{busy ? '…' : `Tocar: ${action.label}`}</span>;
    }
    return <span className="kds-ticket__hint">{action.text}</span>;
  };

  return (
    <div
      className={`kds-ticket ${stateClass}${ringing ? ' kds-ticket--ringing' : ''}${tapAction ? ' kds-ticket--tappable' : ''}${busy ? ' kds-ticket--busy' : ''}`}
      role={tapAction ? 'button' : undefined}
      tabIndex={tapAction ? 0 : undefined}
      aria-label={tapAction ? `${order.areaName} ${order.tableNumber}: ${tapAction.label}` : undefined}
      onClick={tapAction ? run : undefined}
      onKeyDown={tapAction ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          run();
        }
      } : undefined}
    >
      <div className="kds-ticket__head">
        <b>
          {order.areaName} · {order.tableNumber}
          {order.priority === 1 && <FireOutlined className="kds-ticket__fire" />}
        </b>
        <TicketTimer order={order} />
      </div>
      <div className="kds-ticket__meta">
        {order.orderNumber ? `#${order.orderNumber} · ` : ''}{order.waiterName}
      </div>

      <div className="kds-ticket__items">
        <KdsOrderItems order={order} />
      </div>

      {order.kitchenNotes && (
        <div className="kds-ticket__notes">{order.kitchenNotes}</div>
      )}

      <div className="kds-ticket__foot">
        <span className="kds-ticket__state">{isLate ? 'Atrasada' : STATE_LABEL[laneKey] ?? ''}</span>
        {renderFooterAction()}
      </div>
    </div>
  );
};

/** Vista de rieles del KDS: un riel por estado con las comandas colgando, la más vieja a la izquierda. */
const KitchenRailsView: React.FC<KitchenRailsViewProps> = ({ lanes, acceptedOrders, ...handlers }) => (
  <div className="kds-rails">
    {lanes
      .filter(lane => ALWAYS_VISIBLE.has(lane.key) || lane.orders.length > 0)
      .map(lane => (
        <section key={lane.key} className={`kds-rail ${lane.className}`}>
          <div className="kds-lane__head">
            <span className="kds-lane__label">{lane.label}</span>
            <span className="kds-lane__count">{lane.orders.length}</span>
          </div>
          <div className="kds-rail__bar" aria-hidden="true" />
          <div className="kds-rail__track">
            {lane.orders.length === 0 ? (
              <span className="kds-rail__empty">Sin comandas</span>
            ) : (
              lane.orders.map(order => (
                <HangingTicket
                  key={order.id}
                  order={order}
                  laneKey={lane.key}
                  ringing={lane.key === 'new' && handlers.soundEnabled && !acceptedOrders.has(order.id)}
                  handlers={handlers}
                />
              ))
            )}
          </div>
        </section>
      ))}
  </div>
);

export default KitchenRailsView;
